/**
 * Writing form panel component for fill-in-the-blank questions.
 *
 * Displays a text with inline input fields for blanks. Shows numbered
 * blanks with placeholder hints and a progress tracker implemented via
 * the shared `SimulationPanelHeader` + `SimulationTaskProgress` primitives
 * so Reading/Listening/Writing render an identical task-header shape.
 *
 * @module WritingFormPanel
 */

import { useState, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  SimulationPanelHeader,
  SimulationTaskProgress,
} from "@/components/exam/shared";
import { cn } from "@/lib/utils";

interface BlankField {
  id: string;
  expectedAnswer?: string;
  placeholder?: string;
}

interface TextSegment {
  type: "text" | "blank";
  content?: string;
  blankId?: string;
}

interface WritingFormPanelProps {
  readonly segments: TextSegment[];
  readonly blanks: BlankField[];
  readonly answers: Record<string, string>;
  readonly onAnswerChange: (blankId: string, value: string) => void;
  readonly progressCurrent: number;
  readonly progressTotal: number;
  /** Optional element rendered as the Task overview button on the header's right side. */
  readonly headerRightSlot?: ReactNode;
}

export function WritingFormPanel({
  segments,
  blanks,
  answers,
  onAnswerChange,
  progressCurrent,
  progressTotal,
  headerRightSlot,
}: WritingFormPanelProps) {
  const { t } = useTranslation();
  const [focusedBlank, setFocusedBlank] = useState<string | null>(null);

  // Create segments with stable keys to avoid using array index as key
  const segmentsWithKeys = useMemo(() => {
    return segments.map((segment, index) => ({
      ...segment,
      uniqueKey: segment.blankId || `text-${index}`,
    }));
  }, [segments]);

  const title = t("simulation.writing.formPanel.title", {
    defaultValue: "Complete the Text",
  });

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="border-b border-border bg-card/50 shrink-0">
        <SimulationPanelHeader
          title={title}
          rightSlot={headerRightSlot}
          className="border-b-0 pb-2 sm:pb-2"
        />
        <div className="px-3 pb-3 sm:px-6 sm:pb-4">
          <SimulationTaskProgress
            current={progressCurrent}
            total={Math.max(progressTotal, 1)}
          />
        </div>
      </div>

      {/* Form Content */}
      <ScrollArea className="flex-1">
        <div className="p-3 sm:p-6">
          <div className="rounded-lg border border-border bg-card p-3 shadow-sm sm:p-6">
            <form
              autoComplete="off"
              className="contents"
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="font-reading text-foreground leading-[2.8] sm:leading-[2.2] text-sm sm:text-base">
                {segmentsWithKeys.map((segment) => {
                  if (segment.type === "text") {
                    return (
                      <span
                        key={segment.uniqueKey}
                        className="whitespace-pre-wrap"
                      >
                        {segment.content}
                      </span>
                    );
                  }

                  if (segment.type === "blank" && segment.blankId) {
                    const blankIndex =
                      blanks.findIndex((b) => b.id === segment.blankId) + 1;
                    const isFocused = focusedBlank === segment.blankId;
                    const hasValue =
                      answers[segment.blankId]?.trim().length > 0;

                    return (
                      <span
                        key={segment.uniqueKey}
                        className="inline-flex items-baseline gap-0.5 mx-1"
                      >
                        <Input
                          data-writing-blank-id={segment.blankId}
                          value={answers[segment.blankId] || ""}
                          onChange={(e) =>
                            onAnswerChange(segment.blankId, e.target.value)
                          }
                          onFocus={() => setFocusedBlank(segment.blankId)}
                          onBlur={() => setFocusedBlank(null)}
                          autoComplete="off"
                          spellCheck={false}
                          className={cn(
                            "inline-flex h-8 w-24 px-2 py-1 font-ui text-base sm:w-32 lg:text-sm",
                            "border-b-2 border-t-0 border-l-0 border-r-0 rounded-none",
                            "bg-transparent focus:bg-primary/5",
                            "transition-all duration-200",
                            isFocused && "border-primary",
                            hasValue &&
                              !isFocused &&
                              "border-muted-foreground/50",
                            !hasValue &&
                              !isFocused &&
                              "border-muted-foreground/30",
                          )}
                        />
                        <span className="text-[10px] sm:text-xs font-ui font-bold text-primary ml-0.5 whitespace-nowrap self-end -mb-0.5">
                          ({blankIndex})
                        </span>
                      </span>
                    );
                  }

                  return null;
                })}
              </div>
            </form>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export type { BlankField, TextSegment };
